import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { toast } from "@/hooks/use-toast";
import { Money } from "./Money";
import type { PurchaseOrder, PurchaseOrderItem } from "@/features/purchase-orders/types";

interface AdjustUnitCostDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: PurchaseOrderItem | null;
  order: PurchaseOrder;
  /**
   * Current sku_inventory.weighted_average_cost at the PO's location for this
   * SKU, in org base currency. `null` = not yet loaded or no inventory record.
   */
  currentWac: number | null;
  /**
   * Current sku_inventory.current_stock at the PO's location.
   * `null` treated as 0 in the preview math.
   */
  currentStockAtLocation: number | null;
  orgBaseCurrency: string;
}

/**
 * BLU-373 PR3 — Adjust unit cost on a received PO line item.
 *
 * Form layout (top → bottom): old/new comparison → live impact preview →
 * required reason ≥ 5 chars → Cancel + Save buttons.
 *
 * Per DESIGN.md §3.4 the constructive action uses brand-purple; Cancel uses
 * orange-outline. No type-to-confirm — the reason field is the gate.
 */
export function AdjustUnitCostDialog({
  open,
  onOpenChange,
  item,
  order,
  currentWac,
  currentStockAtLocation,
  orgBaseCurrency,
}: AdjustUnitCostDialogProps) {
  const [newCostStr, setNewCostStr] = useState("");
  const [reason, setReason] = useState("");
  const [reasonTouched, setReasonTouched] = useState(false);

  // Reset form whenever the dialog opens for a new item
  useEffect(() => {
    if (open && item) {
      setNewCostStr(item.unit_cost != null ? String(item.unit_cost) : "");
      setReason("");
      setReasonTouched(false);
    }
  }, [open, item]);

  const utils = trpc.useUtils();
  const mutation = trpc.purchaseOrders.adjustReceivedItemCost.useMutation({
    onSuccess: (result) => {
      // Invalidate everything that might display unit_cost, line_cost_total,
      // total_cost, or WAC for this PO. The Suppliers detail page (BLU-373 PR3)
      // also surfaces PO unit costs via getOrdersBySupplier.
      utils.purchaseOrders.getOrders.invalidate();
      utils.purchaseOrders.getOrdersBySupplier.invalidate();
      utils.inventory.getProducts.invalidate();

      const wacUnchanged = result.wacUnchanged === true;
      toast({
        title: wacUnchanged
          ? "Unit cost updated. Stock fully consumed, WAC unchanged."
          : `Unit cost updated. WAC rebased to ${formatNumber(result.newWac ?? null, 2)} ${orgBaseCurrency}.`,
      });
      onOpenChange(false);
    },
    onError: (err) => {
      toast({
        title: "Failed to update unit cost",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const exchangeRate = order.exchange_rate ?? 1;
  const oldUnitCost = item?.unit_cost ?? 0;
  const receivedQty = item?.received_quantity ?? 0;
  const stock = currentStockAtLocation ?? 0;

  const parsedNewCost = useMemo(() => {
    const n = parseFloat(newCostStr);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [newCostStr]);

  const remainingQty = Math.min(receivedQty, Math.max(stock, 0));

  const previewNewWac = useMemo(() => {
    if (parsedNewCost == null) return null;
    if (stock <= 0 || remainingQty <= 0) return currentWac;
    if (currentWac == null) return null;
    const deltaBase = (parsedNewCost - oldUnitCost) * exchangeRate;
    return ((stock * currentWac) + (remainingQty * deltaBase)) / stock;
  }, [parsedNewCost, oldUnitCost, exchangeRate, stock, remainingQty, currentWac]);

  const reasonError = reasonTouched && reason.trim().length < 5
    ? "Reason must be at least 5 characters."
    : null;

  const isInvalid =
    item == null ||
    parsedNewCost == null ||
    parsedNewCost === oldUnitCost ||
    reason.trim().length < 5 ||
    mutation.isPending;

  const handleSubmit = () => {
    if (!item || parsedNewCost == null) return;
    mutation.mutate({
      poItemId: item.id,
      newUnitCost: parsedNewCost,
      reason: reason.trim(),
    });
  };

  const currencySymbol = currencySymbolFor(order.currency);
  // Distinguish "WAC data unknown" from "stock genuinely consumed".
  const hasInventoryData = currentStockAtLocation != null && currentWac != null;
  const fullyConsumed = hasInventoryData && stock <= 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Adjust unit cost</DialogTitle>
          <DialogDescription>
            {order.po_number}
            {item ? `: ${item.sku ?? "SKU"}` : ""}
          </DialogDescription>
        </DialogHeader>

        {item && (
          <div className="space-y-4">
            {/* 1. Old → New cost row — wraps cleanly on narrow viewports */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <div className="text-sm text-muted-foreground tabular-nums">
                Current: {currencySymbol}
                {oldUnitCost.toFixed(2)} {order.currency ?? ""}
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
              <div className="relative flex-1 min-w-[120px] sm:flex-none sm:w-[140px]">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                  {currencySymbol}
                </span>
                <Input
                  type="text"
                  inputMode="decimal"
                  className="h-8 w-full pl-7 tabular-nums text-sm"
                  value={newCostStr}
                  onChange={(e) => setNewCostStr(e.target.value)}
                  placeholder={oldUnitCost.toFixed(2)}
                  aria-label={`New unit cost in ${order.currency ?? "PO currency"}`}
                />
              </div>
            </div>

            {/* 2. Impact preview */}
            <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1.5">
              {!hasInventoryData ? (
                <p className="text-xs text-muted-foreground">
                  The weighted average cost will be rebased on the remaining stock at
                  the receive location after save. The new value will be reported in
                  the confirmation.
                </p>
              ) : fullyConsumed ? (
                <p className="text-xs text-muted-foreground">
                  Stock fully consumed at this location. Cost will be recorded but
                  WAC will not change.
                </p>
              ) : (
                <>
                  <div className="flex justify-between gap-2 tabular-nums">
                    <span className="text-muted-foreground">Stock at location</span>
                    <span>
                      {stock} units @{" "}
                      {currentWac != null ? (
                        <Money
                          amount={currentWac}
                          poCurrency={orgBaseCurrency}
                          exchangeRate={1}
                        />
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between gap-2 tabular-nums">
                    <span className="text-muted-foreground">Units subject to rebase</span>
                    <span>
                      {remainingQty}{" "}
                      <span className="text-muted-foreground">
                        (min of {receivedQty} received, {stock} on hand)
                      </span>
                    </span>
                  </div>
                  <div className="flex justify-between gap-2 tabular-nums font-medium pt-1 border-t border-border/50">
                    <span>New WAC after adjustment</span>
                    <span className="text-threadheads-purple">
                      {previewNewWac != null && parsedNewCost != null && parsedNewCost !== oldUnitCost ? (
                        <>≈ <Money amount={previewNewWac} poCurrency={orgBaseCurrency} exchangeRate={1} /></>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* 3. Required reason */}
            <div className="space-y-1.5">
              <Label htmlFor="adj-cost-reason">
                Reason
                <span className="text-destructive ml-1">*</span>
              </Label>
              <Textarea
                id="adj-cost-reason"
                placeholder="Why is this cost changing? (min. 5 chars)"
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setReasonTouched(true);
                }}
                rows={2}
                className="resize-none text-sm"
              />
              {reasonError && (
                <p className="text-sm text-destructive">{reasonError}</p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="h-8 px-3 text-xs gap-1.5 border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isInvalid}
            className="h-8 px-3 text-xs gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
          >
            {mutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : null}
            Save adjustment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const currencySymbolFor = (code: string | null | undefined): string => {
  switch (code) {
    case "USD":
      return "$";
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    case "AUD":
      return "$";
    default:
      return "";
  }
};

const formatNumber = (n: number | null, digits: number): string =>
  n == null ? "-" : n.toFixed(digits);
